import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import ImageUploader from '../../components/ImageUploader';
import VideoUploader from '../../components/VideoUploader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, DollarSign, Package, Truck, Camera, Film } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'auto_parts', label: 'Auto Parts' },
  { value: 'tools', label: 'Tools & Equipment' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'other', label: 'Other' }
];

const conditions = [
  { value: 'new', label: 'New' },
  { value: 'used', label: 'Used' },
  { value: 'refurbished', label: 'Refurbished' }
];

export default function SellProduct() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    condition: 'used',
    location: '',
    // Shipping options
    offers_shipping: true,
    shipping_price: '',
    offers_local_pickup: true,
    pickup_location: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.price || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/marketplace/products`,
        {
          title: formData.title,
          description: formData.description,
          price: parseFloat(formData.price),
          category: formData.category,
          condition: formData.condition,
          location: formData.location || 'Fort Wayne, IN',
          images: images,
          video: video
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      // Update shipping options
      if (response.data.product_id) {
        await axios.put(
          `${API}/marketplace/products/${response.data.product_id}/shipping`,
          {
            offers_shipping: formData.offers_shipping,
            shipping_price: parseFloat(formData.shipping_price) || 0,
            offers_local_pickup: formData.offers_local_pickup,
            pickup_location: formData.pickup_location,
            ships_from: formData.location || 'Fort Wayne, IN'
          },
          { headers: getAuthHeaders(), withCredentials: true }
        );
      }
      
      toast.success('Product listed successfully!');
      navigate(`/marketplace/product/${response.data.product_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setLoading(false);
    }
  };

  const platformFee = formData.price ? (parseFloat(formData.price) * 0.01).toFixed(2) : '0.00';
  const youReceive = formData.price ? (parseFloat(formData.price) * 0.99).toFixed(2) : '0.00';

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="sell-product-page">
      <Header />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/marketplace')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Marketplace
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-['Chivo']">List an Item for Sale</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      placeholder="What are you selling?"
                      className="mt-1"
                      required
                      data-testid="title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Describe your item in detail..."
                      className="mt-1"
                      rows={5}
                      data-testid="description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="price">Price *</Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          id="price"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.price}
                          onChange={(e) => handleChange('price', e.target.value)}
                          placeholder="0.00"
                          className="pl-10"
                          required
                          data-testid="price-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="category">Category *</Label>
                      <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                        <SelectTrigger className="mt-1" data-testid="category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="condition">Condition</Label>
                      <Select value={formData.condition} onValueChange={(v) => handleChange('condition', v)}>
                        <SelectTrigger className="mt-1" data-testid="condition-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {conditions.map((cond) => (
                            <SelectItem key={cond.value} value={cond.value}>
                              {cond.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="location">Your Location</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => handleChange('location', e.target.value)}
                        placeholder="City, State"
                        className="mt-1"
                        data-testid="location-input"
                      />
                    </div>
                  </div>

                  {/* Shipping Options */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Truck className="w-5 h-5" />
                      Shipping & Pickup Options
                    </h3>
                    
                    <div className="space-y-4">
                      <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <Checkbox
                          checked={formData.offers_shipping}
                          onCheckedChange={(checked) => handleChange('offers_shipping', checked)}
                        />
                        <div className="flex-1">
                          <p className="font-medium">Offer Shipping</p>
                          <p className="text-sm text-gray-500">Ship items to buyers</p>
                          {formData.offers_shipping && (
                            <div className="mt-2">
                              <Label className="text-sm">Shipping Price</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.shipping_price}
                                onChange={(e) => handleChange('shipping_price', e.target.value)}
                                placeholder="0.00 (free shipping)"
                                className="mt-1 w-40"
                              />
                            </div>
                          )}
                        </div>
                      </label>

                      <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <Checkbox
                          checked={formData.offers_local_pickup}
                          onCheckedChange={(checked) => handleChange('offers_local_pickup', checked)}
                        />
                        <div className="flex-1">
                          <p className="font-medium">Local Pickup Available</p>
                          <p className="text-sm text-gray-500">Buyers can pick up in person</p>
                          {formData.offers_local_pickup && (
                            <div className="mt-2">
                              <Label className="text-sm">Pickup Location</Label>
                              <Input
                                value={formData.pickup_location}
                                onChange={(e) => handleChange('pickup_location', e.target.value)}
                                placeholder="General area (e.g., North Fort Wayne)"
                                className="mt-1"
                              />
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Images */}
                  <div className="border-t pt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Camera className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                      <Label className="text-base font-semibold">Product Photos</Label>
                    </div>
                    <ImageUploader
                      images={images}
                      setImages={setImages}
                      maxImages={10}
                      getAuthHeaders={getAuthHeaders}
                    />
                  </div>

                  {/* Video */}
                  <div className="border-t pt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Film className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                      <Label className="text-base font-semibold">Product Video (Optional)</Label>
                    </div>
                    <VideoUploader
                      onUpload={(url) => setVideo(url)}
                      existingVideo={video}
                      onRemove={() => setVideo(null)}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                    data-testid="submit-listing"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'List Item'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Pricing Info */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Pricing Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Your Price</span>
                    <span className="font-medium">${formData.price || '0.00'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Platform Fee (1%)</span>
                    <span className="text-red-600">-${platformFee}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-semibold">You Receive</span>
                    <span className="text-xl font-bold text-green-600">${youReceive}</span>
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 mt-4">
                  A 1% platform fee is deducted when your item sells. 
                  Funds are credited to your wallet after purchase completion.
                </p>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardContent className="py-4">
                <h4 className="font-semibold text-gray-900 mb-2">Tips for a great listing</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Use clear, descriptive titles</li>
                  <li>• Add detailed descriptions</li>
                  <li>• Include high-quality images</li>
                  <li>• Set a competitive price</li>
                  <li>• Offer multiple shipping options</li>
                  <li>• Respond to messages quickly</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
